// Equity curve simple en SVG puro — sin libs adicionales. Muestra P&L
// acumulado a lo largo del tiempo como un area chart. Líneas sutiles
// de referencia (zero line, peak), color verde/rojo según signo del último
// punto.

import type { ReactElement } from 'react';

interface Point {
  date: string;
  pnl: number;
  cumulative: number;
}

interface Props {
  points: Point[];
  height?: number;
}

export function EquityCurve({ points, height = 160 }: Props): ReactElement {
  if (points.length < 2) {
    return (
      <div
        className="flex items-center justify-center text-xs text-ink-subtle font-mono rounded-lg border border-dashed border-surface-border"
        style={{ height }}
      >
        Necesita ≥ 2 trades cerrados para graficar
      </div>
    );
  }

  const W = 1000; // viewBox width — escalable via CSS
  const H = height;
  const padX = 8;
  const padY = 16;

  const innerW = W - padX * 2;
  const innerH = H - padY * 2;

  const min = Math.min(...points.map((p) => p.cumulative), 0);
  const max = Math.max(...points.map((p) => p.cumulative), 0);
  const range = max - min || 1;

  const x = (i: number): number => padX + (i / (points.length - 1)) * innerW;
  const y = (val: number): number => padY + ((max - val) / range) * innerH;

  // Path del area
  const pathD = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(p.cumulative)}`)
    .join(' ');
  const areaD = `${pathD} L${x(points.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;

  const lastValue = points[points.length - 1]!.cumulative;
  const isPositive = lastValue >= 0;
  const strokeColor = isPositive ? 'rgb(16, 185, 129)' : 'rgb(239, 68, 68)';
  const fillColor = isPositive ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)';

  // Y de la línea cero
  const zeroY = y(0);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
    >
      {/* Zero line */}
      <line
        x1={padX}
        y1={zeroY}
        x2={W - padX}
        y2={zeroY}
        stroke="rgb(var(--surface-strong))"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
      {/* Area fill */}
      <path d={areaD} fill={fillColor} />
      {/* Line */}
      <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="2" strokeLinejoin="round" />
      {/* Last point dot */}
      <circle
        cx={x(points.length - 1)}
        cy={y(lastValue)}
        r="4"
        fill={strokeColor}
        stroke="rgb(var(--surface-elevated))"
        strokeWidth="2"
      />
    </svg>
  );
}
